import { api } from './api';
import type { OneEnvelope } from '../types/api';
import type {
  AuthTokens,
  InvitationPreview,
  LoginPayload,
  ProjectRole,
  UserMembership,
} from '../types/data-models';

export interface AcceptedInvitation {
  user: LoginPayload['user'];
  tokens: AuthTokens;
  memberships: UserMembership[];
  invitedProject: {
    id: number;
    name: string;
    tenantId: number;
    tenantName: string;
    role: ProjectRole;
  };
}

/**
 * Invitation landing + acceptance. Both endpoints are public — the invitee may
 * have no account yet — so these calls must work with no bearer token. When the
 * console *does* hold a session, axios attaches it and the backend uses the
 * signed-in identity instead of a Google credential.
 */
export const invitationsService = {
  async preview(token: string): Promise<InvitationPreview> {
    const res = await api.get<OneEnvelope<InvitationPreview>>('/invitations/preview', {
      params: { token },
    });
    const body = res.data?.data;
    if (!body) throw new Error('Invalid invitation response');
    return body;
  },

  /**
   * `idToken` is a Google credential, for a signed-out invitee. Omit it when the
   * user is already signed in as the invited address.
   */
  async accept(token: string, idToken?: string): Promise<AcceptedInvitation> {
    const res = await api.post<
      OneEnvelope<
        LoginPayload & {
          memberships: UserMembership[];
          invitedProject: AcceptedInvitation['invitedProject'];
        }
      >
    >('/invitations/accept', { token, idToken });

    const body = res.data?.data;
    if (!body?.accessToken) throw new Error('Invalid invitation response: missing accessToken');
    return {
      user: body.user,
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: body.memberships ?? [],
      invitedProject: body.invitedProject,
    };
  },
};
